// frontend/src/pages/admin/EmailGroupMemberForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Table, Popconfirm, Select, Typography, Tag, Row, Col, Space, message } from 'antd';
import { UserDeleteOutlined, UserAddOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import './users.css';

const { Option } = Select;
const { Text } = Typography;

const EmailGroupMemberForm = ({ visible, emailGroup, onCancel, onSuccess }) => {
  const [members, setMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  const notificationApi = useNotificationApi();

  // Standardized notification functions
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

const fetchMembers = useCallback(async () => {
  if (!emailGroup?.id) {
    setMembers([]);
    return;
  }

  setLoadingMembers(true);
  try {
    const response = await api.get(`/api/email-groups/${emailGroup.id}/members`);
    // Check the actual response structure
    console.log('Members API response:', response.data);
    
    // Use the mapped data directly if the controller is already transforming it
    setMembers(response.data.data || []);
  } catch (error) {
    console.error('Failed to fetch group members:', error);
    showError('Failed to Load Members', error.response?.data?.message || 'Failed to load group members.');
  } finally {
    setLoadingMembers(false);
  }
}, [emailGroup?.id, showError]);

const fetchAvailableUsers = useCallback(async () => {
  if (!emailGroup?.id) return;

  setLoadingUsers(true);
  try {
    const response = await api.get(`/api/email-groups/${emailGroup.id}/available-users`);
    console.log('Available users response:', response.data);
    
    const filteredUsers = (response.data.data || [])
      .filter(user => ['internal_staff', 'partner'].includes(user.userType?.toLowerCase()))
      .map(user => ({
        id: user.userId || user.id, // Handle both formats
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        userType: user.userType
      }));

    setAvailableUsers(filteredUsers);
  } catch (error) {
    console.error('Failed to fetch available users:', error);
    showError('Failed to Load Users', error.response?.data?.message || 'Failed to load available users.');
  } finally {
    setLoadingUsers(false);
  }
}, [emailGroup?.id, showError]);

  useEffect(() => {
    if (visible && emailGroup?.id) {
      fetchMembers();
      fetchAvailableUsers();
      setSelectedUserId(null);
    } else if (!visible) {
      setMembers([]);
      setAvailableUsers([]);
      setSelectedUserId(null);
    }
  }, [visible, emailGroup?.id, fetchMembers, fetchAvailableUsers]);

  const handleAddMember = async () => {
    if (!selectedUserId || !emailGroup?.id) {
      showError('Selection Required', 'Please select a user to add.');
      return;
    }

    setAddingMember(true);
    try {
      await api.post(`/api/email-groups/${emailGroup.id}/members`, { userId: selectedUserId });
      showSuccess('Member Added', 'Member added successfully!');
      setSelectedUserId(null);
      fetchMembers();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Failed to add member:', error);
      const errorMessage = error.response?.status === 409 
        ? 'This user is already a member of this group.'
        : error.response?.data?.message || 'Failed to add member.';
      showError('Add Failed', errorMessage);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!emailGroup?.id) {
      showError('Invalid Operation', 'Cannot remove member: No email group selected.');
      return;
    }

    try {
      await api.delete(`/api/email-groups/${emailGroup.id}/members/${memberId}`);
      showSuccess('Member Removed', 'Member removed successfully!');
      fetchMembers();
      fetchAvailableUsers();
    } catch (error) {
      console.error('Failed to remove member:', error);
      showError('Remove Failed', error.response?.data?.message || 'Failed to remove member.');
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.firstName || ''} ${record.lastName || ''}`,
      sorter: (a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`;
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`;
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
    },
    {
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (userType) => userType ? (
        <Tag color="blue">
          {userType.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
        </Tag>
      ) : 'N/A',
      filters: [
        { text: 'Internal Staff', value: 'internal_staff' },
        { text: 'Partner', value: 'partner' },
      ],
      onFilter: (value, record) => (record.userType || '').toLowerCase() === value.toLowerCase(),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        isActive ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">Inactive</Tag>
        )
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title={`Remove ${record.firstName} ${record.lastName} from this group?`}
          onConfirm={() => handleRemoveMember(record.id)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Button
            type="link"
            danger
            icon={<UserDeleteOutlined />}
            aria-label={`Remove ${record.firstName} ${record.lastName}`}
            title="Remove Member"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title={`Manage Members for: ${emailGroup?.name || 'Loading...'}`}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          Done
        </Button>,
      ]}
      width={900}
      centered
      maskClosable={false}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Text strong>Add Member:</Text>
          </Col>
          <Col span={16}>
            <Select
              showSearch
              placeholder="Select a user to add"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children && option.children.toLowerCase().includes(input.toLowerCase())
              }
              loading={loadingUsers}
              value={selectedUserId}
              onChange={setSelectedUserId}
              style={{ width: '100%' }}
            >
              {availableUsers.map((user) => (
                <Option key={user.id} value={user.id}>
                  {`${user.firstName} ${user.lastName} (${user.email})`}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleAddMember}
              loading={addingMember}
              block
            >
              Add
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={members}
          rowKey="id"
          loading={loadingMembers}
          pagination={{ pageSize: 5 }}
          scroll={{ y: 300 }}
          locale={{
            emptyText: loadingMembers ? 'Loading Members...' : 'No members found for this group.',
          }}
        />

        <div style={{ textAlign: 'right' }}>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => {
              fetchMembers();
              fetchAvailableUsers();
            }}
            loading={loadingMembers || loadingUsers}
          >
            Refresh Members
          </Button>
        </div>
      </Space>
    </Modal>
  );
};

export default EmailGroupMemberForm;